import { useCallback, useMemo, useState } from 'react';
import { isAssistantMessage } from './utils/domain/messageUtils';
import { useLiveChat } from './useChatServiceProvider/infra/chat/useLiveChat';
import { useChatServiceDuplexerConnector } from './useChatServiceDuplexer/useChatServiceDuplexer';
import { useProcessFileAttachment } from './useChatServiceProvider/infra/chat/processFileAttachment/useProcessFileAttachment';
import { useStreaming } from './useStreaming/useStreaming';
import { useTextToSpeech, useSpeechToText } from './useChatServiceProvider/infra/chat/speech';
import { useConversationStatusMessage } from './useConversationStatusMessage/useConversationStatusMessage';
import { useQuickReplySuggestions } from './useQuickReplySuggestions/useQuickReplySuggestions';
import { useSideEffectsConnection } from './useStreaming/useSideEffectsConnection';
import { useAbortAssistantMessageGeneration } from './useChatServiceProvider/infra/chat/hooks/useAbortAssistantMessageGeneration';
import { useMessages } from './useChatServiceProvider/hooks/useMessages';
export const useChatServiceProvider = _ref => {
  let {
    enableLogging = false,
    enablePolling = true,
    instanceIdForSideEffects,
    pageUrl,
    pageHtml,
    pageBlob,
    httpClient,
    reference,
    toolRegistry,
    onError
  } = _ref;
  const [loadingsCount, setLoadingsCount] = useState(0);
  const processFileAttachment = useProcessFileAttachment({
    httpClient
  });
  const {
    getSpeechFromText: textToSpeech
  } = useTextToSpeech({
    httpClient
  });
  const {
    getTextFromSpeech: speechToText
  } = useSpeechToText({
    httpClient
  });
  const {
    conversationId,
    messages,
    requests,
    sendMessage,
    sendAssistantMessage: sendAssistantMessageFromLiveChat,
    loadMoreMessages,
    refetchMessages,
    startChat,
    clearChat
  } = useLiveChat({
    httpClient,
    reference,
    enableLogging,
    enablePolling,
    toolRegistry,
    setLoadingsCount,
    pageUrl,
    pageHtml,
    pageBlob,
    onError
  });
  const connection = useSideEffectsConnection({
    instanceIdForSideEffects
  });
  useChatServiceDuplexerConnector(connection);
  const latestUserMessageId = useMemo(() => {
    var _messages$slice$rever;
    return (_messages$slice$rever = messages.slice().reverse().find(isAssistantMessage)) == null ? void 0 : _messages$slice$rever.userMessageId;
  }, [messages]);
  const {
    statusMessages,
    statusMessagesEntries
  } = useConversationStatusMessage({
    conversationId,
    connection
  });
  const {
    quickReplySuggestions
  } = useQuickReplySuggestions({
    connection,
    conversationId
  });
  const {
    streamingMessages
  } = useStreaming({
    connection,
    conversationId
  });
  const wrapWithLoading = async fn => {
    setLoadingsCount(prev => prev + 1);
    try {
      return await fn();
    } finally {
      setLoadingsCount(prev => prev - 1);
    }
  };
  const agentName = 'agentName' in reference ? reference.agentName : null;
  const sendAssistantMessage = useCallback(async message => wrapWithLoading(() => sendAssistantMessageFromLiveChat(message, agentName)), [agentName, sendAssistantMessageFromLiveChat]);
  const sendUserMessage = useCallback(async (userMessageContent, sendMessageOptions) => {
    await wrapWithLoading(() => sendMessage(userMessageContent, agentName || undefined, sendMessageOptions));
  }, [agentName, sendMessage]);
  const startChatWithTools = useCallback(async (startStrategy, options) => {
    const startedConversationId = await startChat(startStrategy);
    if (startStrategy.type === 'new-conversation' && options != null && options.firstUserMessage) {
      sendUserMessage(options.firstUserMessage, {
        ...options.sendMessageOptions,
        metadata: {
          pageUrl,
          pageHtml,
          pageBlob
        }
      });
    }
    return startedConversationId;
  }, [startChat, sendUserMessage, pageUrl, pageHtml, pageBlob]);
  const createConversation = useCallback(async _ref2 => {
    let {
      firstUserMessage,
      origin,
      sendMessageOptions
    } = _ref2;
    clearChat();
    return startChatWithTools({
      type: 'new-conversation',
      origin
    }, {
      firstUserMessage,
      sendMessageOptions
    });
  }, [clearChat, startChatWithTools]);
  const loadConversation = useCallback(async conversation => {
    clearChat();
    return startChatWithTools({
      type: 'existing-conversation',
      id: conversation.id
    });
  }, [clearChat, startChatWithTools]);
  const {
    abortAssistantMessageGeneration,
    abortAssistantMessageGenerationStatus
  } = useAbortAssistantMessageGeneration({
    httpClient
  });
  const onStopButtonClick = useCallback(async () => {
    const userMessageId = latestUserMessageId;
    if (userMessageId) {
      await abortAssistantMessageGeneration(userMessageId);
    }
  }, [abortAssistantMessageGeneration, latestUserMessageId]);
  const isLoading = loadingsCount > 0;
  const lastStatusMessage = latestUserMessageId ? statusMessages[latestUserMessageId] ?? '' : '';
  const lastQuickReplySuggestion = latestUserMessageId ? quickReplySuggestions[latestUserMessageId] ?? [] : [];
  const lastStreamingMessage = latestUserMessageId ? streamingMessages[latestUserMessageId] ?? '' : '';
  const finalMessages = useMessages({
    messages,
    abortAssistantMessageGenerationStatus,
    statusMessagesEntries,
    streamingMessages
  });
  return {
    isLoading,
    conversationId,
    createConversation,
    loadConversation,
    loadMoreMessages,
    refetchMessages,
    sendUserMessage,
    sendAssistantMessage,
    statusMessage: lastStatusMessage,
    quickReplySuggestions: lastQuickReplySuggestion,
    messages: finalMessages,
    messageRequests: requests,
    clearChat,
    textToSpeech,
    speechToText,
    processFileAttachment,
    streamingMessage: lastStreamingMessage,
    onStopButtonClick
  };
};
//# sourceMappingURL=useChatServiceProvider.js.map